// src/pages/SupplierRegisterPage.jsx
import React, { useState, useRef } from "react";
import {track} from '../../analytics/service';
import RegistrationMarketingChoices,{useRegistrationMarketing} from '../../components/auth/RegistrationMarketingChoices';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getRedirectFromSearch, withRedirect } from "../../utils/authRedirect";
import "../css/AuthPages.css";
import {
  validateEmail,
  validateCAP,
  validateProvincia,
  validatePIVA,
  validateCodiceFiscale,
  passwordStrength,
} from "../../utils/validators";

import CityField from "../../components/CityField";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const isEmail = (v) => /\S+@\S+\.\S+/.test(v);
const isCAP = (v) => /^\d{5}$/.test(v);
const isProv = (v) => /^[A-Za-z]{2}$/.test(v);
const isPIVA = (v) => !v || /^\d{11}$/.test(v);

export default function SupplierRegisterPage() {
  const analyticsStarted = useRef(false);
  const marketing=useRegistrationMarketing();
  const [user, setUser] = useState({
    name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [sup, setSup] = useState({
    nome: "",
    last_name: "",
    indirizzo: "",
    cap: "",
    citta: "",
    provincia: "",
    telefono: "",
    codice_fiscale: "",
    partita_iva: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // hard confirm: devi scegliere la città dall’elenco, come RegisterPage
  const [cityConfirmed, setCityConfirmed] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  // conservato nei link verso login / registrazione cliente
  const redirectParam = getRedirectFromSearch(location.search);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    } else {
      setLogoFile(null);
      setLogoPreview("");
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return value ? "" : "Nome referente obbligatorio";
      case "last_name": // 👈 nuovo
        return value ? "" : "Cognome referente obbligatorio";
      case "email":
        return !value || isEmail(value) ? "" : "Email non valida";
      case "password":
        return value && value.length >= 6 ? "" : "Minimo 6 caratteri";
      case "nome":
        return value ? "" : "Alias obbligatorio";
      case "telefono":
        return value ? "" : "Telefono obbligatorio";
      case "indirizzo":
        return value ? "" : "Indirizzo obbligatorio";
      case "cap":
        return isCAP(value) ? "" : "CAP a 5 cifre";
      case "citta":
        return value ? "" : "Città obbligatoria";
      case "provincia":
        return isProv(value) ? "" : "Provincia (es. RM)";
      case "codice_fiscale":
        if (!value) return "Codice fiscale obbligatorio";
        return validateCodiceFiscale(value) ? "" : "Codice fiscale non valido";
      case "partita_iva":
        return isPIVA(value) ? "" : "Partita IVA di 11 cifre";
      default:
        return "";
    }
  };

  const onUser = (e) => {
    const { name, value } = e.target;
    setUser((u) => ({ ...u, [name]: value }));
    if (touched[name])
      setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
  };

  const setSupField = (name, value, { markTouched = false } = {}) => {
    setSup((s) => ({ ...s, [name]: value }));

    if (markTouched) {
      setTouched((t) => ({ ...t, [name]: true }));
    }

    if (touched[name] || markTouched) {
      setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
    }
  };

  const onSup = (e) => {
    const { name, value } = e.target;
    setSupField(name, value);
  };

  const onCap = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 5);
    setSupField("cap", v);
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    const value = user[name] ?? sup[name] ?? "";
    setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setShowSuccess(false);

    const required = {
      name: user.name,
      last_name: user.last_name,
      password: user.password,
      nome: sup.nome,
      telefono: sup.telefono,
      indirizzo: sup.indirizzo,
      cap: sup.cap,
      citta: sup.citta,
      provincia: sup.provincia,
      codice_fiscale: sup.codice_fiscale,
    };
    const optional = {
      email: user.email,
      partita_iva: sup.partita_iva,
    };

    const nextErr = {
      ...Object.fromEntries(
        Object.entries(required).map(([k, v]) => [k, validateField(k, v)]),
      ),
      ...Object.fromEntries(
        Object.entries(optional).map(([k, v]) => [k, validateField(k, v)]),
      ),
    };

    setTouched((t) => ({
      ...t,
      ...Object.fromEntries(Object.keys(nextErr).map((k) => [k, true])),
    }));
    setErrors(nextErr);
    if (Object.values(nextErr).some(Boolean)) return;

    // stesso controllo di RegisterPage: non basta digitare, bisogna scegliere dall’elenco
    if (!cityConfirmed) {
      setErrors((prev) => ({
        ...prev,
        citta: "Seleziona una città dall'elenco",
      }));
      setTouched((prev) => ({ ...prev, citta: true }));
      return;
    }

    const payload = {
      name: user.name,
      last_name: user.last_name || undefined,
      email: user.email || undefined,
      password: user.password,
      phone: sup.telefono || undefined,
      codice_fiscale: sup.codice_fiscale,
      address: sup.indirizzo,
      cap: sup.cap,
      citta: sup.citta,
      provincia: sup.provincia,
      role: "fornitore",
      marketing: marketing.payload,
      fornitore: {
        nome: sup.nome,
        indirizzo: sup.indirizzo,
        cap: sup.cap,
        citta: sup.citta,
        provincia: sup.provincia,
        telefono: sup.telefono,
        partita_iva: sup.partita_iva || undefined,
      },
    };

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {track('registration_result',{outcome:'failure',error_code:'request_rejected',account_type:'supplier'});return setError(data.error || "Errore nella registrazione");}

      setShowSuccess(true);
      track('registration_result',{outcome:'success',account_type:'supplier'});
      setTimeout(() => navigate(withRedirect("/login", redirectParam)), 1800);
    } catch {
      track('registration_result',{outcome:'failure',error_code:'network',account_type:'supplier'});
      setError("Errore di rete");
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength(user.password);

  return (
    <>
      <AuthSplitLayout
        heroTitle="Vuoi diventare fornitore?"
        heroSubtitle="Non perdere questa occasione."
        cardTitle="Registrazione fornitore"
        cardSubtitle="Crea il tuo profilo ed entra nella rete StirUp."
        showLoginLink
      >
        <form onSubmit={submit} noValidate onChangeCapture={event=>{
          if (!analyticsStarted.current && event.target.value && !['checkbox','file'].includes(event.target.type)) {
            analyticsStarted.current=true;track('registration_started',{account_type:'supplier'});
          }
        }}>
          {error && (
            <div className="auth-error" role="alert">
              {error}
            </div>
          )}

          <div className="form-grid two">
            <div className="field">
              <label htmlFor="name">Nome referente</label>
              <input
                id="name"
                name="name"
                value={user.name}
                onChange={onUser}
                onBlur={onBlur}
                required
                className={touched.name && errors.name ? "input-invalid" : ""}
              />
              {touched.name && errors.name && (
                <div className="error-text">{errors.name}</div>
              )}
            </div>

            <div className="field">
              <label htmlFor="last_name">Cognome referente</label>
              <input
                id="last_name"
                name="last_name"
                value={user.last_name}
                onChange={onUser}
                onBlur={onBlur}
                required
                className={
                  touched.last_name && errors.last_name
                    ? "input-invalid"
                    : ""
                }
              />
              {touched.last_name && errors.last_name && (
                <div className="error-text">{errors.last_name}</div>
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="nome">Alias</label>
            <input
              id="nome"
              name="nome"
              value={sup.nome}
              onChange={onSup}
              onBlur={onBlur}
              required
              className={touched.nome && errors.nome ? "input-invalid" : ""}
            />
            {touched.nome && errors.nome && (
              <div className="error-text">{errors.nome}</div>
            )}
          </div>

          <div className="field">
            <label htmlFor="email">Email (opzionale)</label>
            <input
              id="email"
              type="email"
              name="email"
              value={user.email}
              onChange={onUser}
              onBlur={onBlur}
              className={touched.email && errors.email ? "input-invalid" : ""}
            />
            {touched.email && errors.email && (
              <div className="error-text">{errors.email}</div>
            )}
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <div className="pwd-wrap">
              <input
                id="password"
                type={showPwd ? "text" : "password"}
                name="password"
                value={user.password}
                onChange={onUser}
                onBlur={onBlur}
                required
                placeholder="Almeno 6 caratteri"
                className={
                  touched.password && errors.password ? "input-invalid" : ""
                }
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? "Nascondi password" : "Mostra password"}
              >
                {showPwd ? "🙈" : "👁️"}
              </button>
            </div>
            <div className="pwd-meter" aria-hidden>
              <div
                className="pwd-meter-bar"
                style={{
                  width: `${(strength / 4) * 100}%`,
                }}
              />
            </div>
          </div>

          <h4 className="reg-section-title">Dati fornitore</h4>

          <div className="field">
            <label htmlFor="telefono">Telefono</label>
            <input
              id="telefono"
              name="telefono"
              value={sup.telefono}
              onChange={onSup}
              onBlur={onBlur}
              required
              inputMode="tel"
              autoComplete="tel"
              className={
                touched.telefono && errors.telefono ? "input-invalid" : ""
              }
            />
            {touched.telefono && errors.telefono && (
              <div className="error-text">{errors.telefono}</div>
            )}
          </div>

          <div className="form-grid two">
            <div className="field">
              <label htmlFor="codice_fiscale">Codice Fiscale</label>
              <input
                id="codice_fiscale"
                name="codice_fiscale"
                value={sup.codice_fiscale}
                onChange={onSup}
                onBlur={onBlur}
                required
                className={
                  touched.codice_fiscale && errors.codice_fiscale
                    ? "input-invalid"
                    : ""
                }
              />
              {touched.codice_fiscale && errors.codice_fiscale && (
                <div className="error-text">{errors.codice_fiscale}</div>
              )}
            </div>
            <div className="field">
              <label htmlFor="partita_iva">Partita IVA (opzionale)</label>
              <input
                id="partita_iva"
                name="partita_iva"
                value={sup.partita_iva}
                onChange={onSup}
                onBlur={onBlur}
                placeholder="11 cifre"
                className={
                  touched.partita_iva && errors.partita_iva
                    ? "input-invalid"
                    : ""
                }
              />
              {touched.partita_iva && errors.partita_iva && (
                <div className="error-text">{errors.partita_iva}</div>
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="indirizzo">Indirizzo</label>
            <input
              id="indirizzo"
              name="indirizzo"
              value={sup.indirizzo}
              onChange={onSup}
              onBlur={onBlur}
              required
              autoComplete="street-address"
              className={
                touched.indirizzo && errors.indirizzo ? "input-invalid" : ""
              }
            />
            {touched.indirizzo && errors.indirizzo && (
              <div className="error-text">{errors.indirizzo}</div>
            )}
          </div>

          <div className="field field-with-suggestions">
            <CityField
              label="Città"
              value={sup.citta}
              provinciaValue={sup.provincia}
              capValue={sup.cap}
              hardConfirm
              confirmed={cityConfirmed}
              setConfirmed={setCityConfirmed}
              error={touched.citta && errors.citta ? errors.citta : ""}
              resetProvinciaOnType={true}
              resetCapOnType={true}
              onChangeCity={(v, meta) => {
                setSup((s) => ({
                  ...s,
                  citta: v,
                  provincia: meta?.resetProvincia ? "" : s.provincia,
                  cap: meta?.resetCap ? "" : s.cap,
                }));

                setCityConfirmed(false);

                if (touched.citta) {
                  setErrors((p) => ({
                    ...p,
                    citta: validateField("citta", v),
                  }));
                }
              }}
              onApplyCity={({ name, provincia, cap }) => {
                setSup((s) => ({
                  ...s,
                  citta: name || "",
                  provincia: (provincia || "").toUpperCase(),
                  cap: cap || "",
                }));

                setCityConfirmed(true);

                setTouched((t) => ({
                  ...t,
                  citta: true,
                  provincia: true,
                  cap: true,
                }));

                setErrors((e) => ({
                  ...e,
                  citta: "",
                  provincia: "",
                  cap: "",
                }));
              }}
              inputClassName={
                touched.citta && errors.citta ? "input-invalid" : ""
              }
              showEmptyHint
            />
          </div>

          <div className="form-grid two">
            <div className="field">
              <label htmlFor="provincia">Provincia</label>
              <input
                id="provincia"
                name="provincia"
                value={sup.provincia}
                readOnly
                placeholder="es. MO"
                className={
                  touched.provincia && errors.provincia
                    ? "input-invalid"
                    : ""
                }
              />
              {touched.provincia && errors.provincia && (
                <div className="error-text">{errors.provincia}</div>
              )}
            </div>

            <div className="field">
              <label htmlFor="cap">CAP</label>
              <input
                id="cap"
                name="cap"
                value={sup.cap}
                onChange={onCap}
                onBlur={onBlur}
                inputMode="numeric"
                required
                className={touched.cap && errors.cap ? "input-invalid" : ""}
              />
              {touched.cap && errors.cap && (
                <div className="error-text">{errors.cap}</div>
              )}
            </div>
          </div>

          {/* Upload logo coerente con il resto */}
          <div className="field">
            <label htmlFor="logo">Foto / Logo</label>
            <div className="file-upload-row">
              <label htmlFor="logo" className="btn-file">
                📷 Seleziona immagine
              </label>
              <span className="file-upload-name">
                {logoFile ? logoFile.name : "Nessun file selezionato"}
              </span>
              <input
                id="logo"
                name="logo"
                type="file"
                accept="image/*"
                onChange={onLogoChange}
                className="file-input-hidden"
              />
            </div>
            {logoPreview && (
              <div className="logo-preview">
                <img src={logoPreview} alt="Anteprima logo" />
              </div>
            )}
          </div>

          <RegistrationMarketingChoices value={marketing} disabled={loading}/>
          <div className="reg-actions">
            <button
              type="submit"
              className="reg-submit-btn"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? "Invio…" : "Invia richiesta"}
            </button>
          </div>

          <div className="reg-bottom-links">
            <p className="muted">
              Sei un cliente?{" "}
              <Link
                className="auth-link"
                to={withRedirect("/register", redirectParam)}
              >
                Registrati come cliente →
              </Link>
            </p>
          </div>
        </form>
      </AuthSplitLayout>

      {showSuccess && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card">
            <div className="modal-icon">🎉</div>
            <h3>Richiesta inviata!</h3>
            <p>
              Attiveremo il profilo dopo approvazione. Ti reindirizziamo al
              login…
            </p>
            <button
              className="reg-submit-btn"
              onClick={() => navigate(withRedirect("/login", redirectParam))}
            >
              Vai al login
            </button>
          </div>
        </div>
      )}
    </>
  );
}
